import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ChangeAgentRequest, ChatConfigRequest, ChatConfigResponse, ChatOption, ChatResponse, Message, MessageData } from '../../models/chat.models';
import { ChatService } from '../../services/chat.service';
import { ChatEvent, } from '../../models/event.models';
import { MarkdownService } from 'ngx-markdown';
import { SpeechRecognizer, SpeechSynthesizer, AudioConfig, ResultReason, SpeakerAudioDestination } from "microsoft-cognitiveservices-speech-sdk";
import { ChatResponseType, ChatStatus, FeedbackState, IdleStateMessage, IdleStatusType, IdleTimeOut, ReservedTags, Role } from '../../enums/chat.enum';
import { SubscriptionHandlerDirective } from '../../directives/subscription-handler.directive';
import { AgentType, EventType, ActionType} from '../../enums/event.enum';
import { catchError, tap } from 'rxjs/operators';
import { EMPTY, Observable } from 'rxjs';
import * as uuid from 'uuid';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { ChatStreamService } from '../../services/chat-stream.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AuthenticationService } from '../../services/authentication.service';
import { ChatToggleComponent } from '../chat-toggle/chat-toggle.component';
import { HostRoute } from '../../enums/subscription-enum';
import { ConfigurationService } from '../../services/configuration.service';
import { SubscriptionConfig } from '../../models/subscription.models';
import { DashboardWidgetActionResponse } from '../../models/widget.models';
import { ChatEventsService } from '../../services/chat-events.service';

const DEFAULT_CHAT_AGENT_NAME = "Saffron AI";
const MESSAGE_FIELD = "message";
const FILE_FIELD = "file";
const SITE_ID_FIELD = "siteId";
const MENTIONS_FIELD = "mentions";
const CHAT_PROFILE_FIELD = "profileId";
const CONTEXT = "context"

@Component({
  selector: 'app-generative-widget-command-layout',
  templateUrl: './generative-widget-command-layout.component.html',
  styleUrls: ['./generative-widget-command-layout.component.scss'],
  providers: [ChatService, ChatStreamService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false

})
export class GenerativeWidgetCommandLayoutComponent extends SubscriptionHandlerDirective implements OnInit {
  @Input() buildingEntityList: any[];
  @Input() isAndroid: boolean = false;
  @Input() config: ChatConfigRequest;
  @Input() bearerToken: string;
  @Input() subscriptionConfig: SubscriptionConfig;
  @Input() isAIChatSubscriptionActive: boolean = true;
  @Input() userName: string;
  @Input() sendIconEnabled: boolean = false;
  @Input() chatInputPlaceholderText: string = "Describe what would you like to update...";

  @Output() chatEvent: ChatEvent;
  @Output() chartGenerated = new EventEmitter<any>();
  @Output() recommendCharts = new EventEmitter<any>();
  @Output() variationGenerated = new EventEmitter<any>();
  private responseCounter = 0;

  @ViewChild('chatContentElement') private chatContentElement: ElementRef;
  @ViewChild('chatInput') private chatInput: ChatInputComponent;
  @ViewChild('chatToggle') private chatToggle: ChatToggleComponent;

  private _previousScrollHeight: number = 0;

  maximize: boolean = false;
  collapse: boolean = false;

  loading: boolean = false;
  feedbackState: FeedbackState;

  messages: Message[] = [];
  activeMessage: Message;

  initialMessages = [];

  
  aiAgentName: string = DEFAULT_CHAT_AGENT_NAME;

  showFeedbackPrompt: boolean;

  // Text & Speech options
  speechEnabled: boolean = false;

  chatStatus: ChatStatus = ChatStatus.CHAT_INIT;

  // Live Agent Chat
  chatAgentType: AgentType = AgentType.AI;
  liveAgentName: string;
  liveAgentTypingLoader: boolean = false;
  liveAgentConversationId: string;

  // Mention Options
  mentionOptions: any[] = [];
  resetLastActiveTimeOutInterval: ReturnType<typeof setInterval> | undefined;
  resetLiveAgentReplyTimeOutInterval: ReturnType<typeof setInterval> | undefined;
  resetLiveAgentPendingTimeOutInterval: ReturnType<typeof setInterval> | undefined;
  awaitingLiveAgentTime = new Date();
  awaitingReplyTime = new Date();
  IdleState = IdleStatusType;
  disableInput: boolean = false;
  websocketUrl: string;
  chatScreenConfig: ChatConfigResponse;

  constructor(
    private sanitizer: DomSanitizer,
    private chatService: ChatService,
    private markdownService: MarkdownService,
    private configService: ConfigurationService,
    private chatEventsService: ChatEventsService,
    private authenticationService: AuthenticationService,
    private chatStreamService: ChatStreamService
  ) {
    super();
  }

  ngOnInit(): void {
    this.responseCounter = 0;
    this.loading = true;
    if (this.bearerToken) {
      this.authenticationService.bearerToken = this.bearerToken;
    }

    this.subs.sink = this.chatService.initializeSession(this.config).pipe(
      tap((response) => {
        this.chatStatus = ChatStatus.CHAT_ACTIVE;
        this.chatService.setSessionId(response.sessionId);
        this.handleAgentResponse(response);
        this.loading = false;
      }),
      tap((response) => {
        this.subs.sink = this.chatStreamService.connect(response.sessionId, this.config.chatUrl).pipe(
          tap((data) => {
            const response = data as ChatResponse;
            this.handleAgentResponse(response);
          })
        ).subscribe();
      })
    ).subscribe(() => {
      // Set interval to check last active time every one minute.
      this.startLastActiveTimeCheck();
    }, (error: any) => {
      this.loading = false;
      this.handleErrorResponse(error);
    });

  } 
  
  get isFacilisightPortal(): boolean {
    return this.configService.getConfig("appName") === HostRoute.FACILISIGHT;
  }

  get activeAgentName(): string {
    return this.chatAgentType == AgentType.AI ? this.aiAgentName : this.liveAgentName;
  }

  get selectedFile(): File | Blob | null {
    return this.chatInput?.selectedFile || this.chatInput?.selectedPhoto;
  };

  connectWebsocket(sessionId: string) {
    this.subs.sink = this.chatStreamService.connect(sessionId, this.config.chatUrl).pipe(
      tap((data) => {
        const response = data as ChatResponse;
        this.handleAgentResponse(response);
      })
    ).subscribe();
  }

  /**
   * Invoked when the user selects an option from the quick select menu.
   * Sets the input text based on the selected option.
   * If the option is vision-related, open the file upload menu.
   * If the option contains an entity reference, also open the mentions menu.
   * Otherwise, auto-submit the message.
   * @param option 
   */
  onSelectOption(option: ChatOption) {
    let placeholderText = option.userInput;
    if (placeholderText.includes(ReservedTags.ENTITY_REF)) {
      placeholderText = placeholderText.replace(ReservedTags.ENTITY_REF, "@");
      setTimeout(() => {
        this.chatInput.openMentionsMenu();
      })
    }

    if (option.presentationData?.openFileUploadMenu) {
      this.chatInput.showUploadOptions = true; // show the file upload menu.
      // opens directly default (ios/android) upload file menu for mobile app.
      this.chatInput.setInputText(placeholderText);
      this.chatInput.showUserInputOptions = false; // hide the user-input options since user has already selected a quick-select prompt.
    } else {
      this.chatInput.setInputText(placeholderText);
      this.chatInput.showUploadOptions = false;
      // auto submit for non-image related quick select options.
      this.onSubmit();
    }
  }

  onToggleAgentType(agentType: AgentType) {
    this.chatStatus = ChatStatus.WAITING_FOR_AGENT;
    this.loading = true;
    const request: ChangeAgentRequest = { profileId: this.config.profileId, siteId: this.config.siteId };
    if (agentType == AgentType.HUMAN) {
      this.awaitingLiveAgentTime = new Date();
    } else {
      clearInterval(this.resetLiveAgentPendingTimeOutInterval); // clearing the timeout on toggle back to AI.
      clearInterval(this.resetLiveAgentReplyTimeOutInterval); // clearing the timeout on toggle back to AI.
    }
    this.startLastActiveTimeCheck(); // setting the interval again to check last active time every one minute. on click of live agent.
    this.chatService.transferAgent(this.chatAgentType, request).pipe(
      tap((response) => {
        this.loading = false;
        if (agentType == AgentType.AI) {
          // AI agent will be connected immediately
          this.chatStatus = ChatStatus.CHAT_ACTIVE;
          this.chatAgentType = AgentType.AI;
          this.chatStreamService.closeConnection();
        } else {
          // Wait for live agent to connect. Chat status will be updated to ACTIVE when the live agent sends a message.
          this.chatStatus = ChatStatus.WAITING_FOR_AGENT;
          this.ensureActiveWebsocketConnection(response.sessionId);
        }
        this.handleAgentResponse(response);
      })
    ).subscribe();
  }

  /**
   * Ensures that a WebSocket connection is established.
   * If the `connection$` property of the `chatStreamService` is not defined,
   * it initiates a connection to the WebSocket server using the provided URL.
   */
  ensureActiveWebsocketConnection(sessionId: string) {
    if (!this.chatStreamService.connection$) {
      this.connectWebsocket(sessionId);
    }
  }

  onCollapse() {
    this.collapse = true;
  }

  onReset() {
    this.maximize = false;
  }

  onExpand() {
    this.collapse = false;
  }

  onMaximize() {
    this.maximize = true;
  }

  clearTimeOutIntervals() {
    clearInterval(this.resetLiveAgentPendingTimeOutInterval);
    clearInterval(this.resetLastActiveTimeOutInterval);
    clearInterval(this.resetLiveAgentReplyTimeOutInterval);
  }

  mentions: any[] = [];
  onMentions(mentions: any[]) {
    this.mentions = mentions;
  }

  onSubmit() {
    const inputText = this.chatInput.getInputText();
    if (inputText.length > 0) {
      this.submitMessage(inputText);
      this.chatInput.resetInput(); // This feels dirty. Maybe we handle this via events instead?
    }
  }

  submitMessageToAgent(userMessage: string): Observable<ChatResponse> {
    let widgetData = structuredClone(this.config?.taskData ? this.config?.taskData?.data : []);
    if(widgetData){
      delete widgetData?.data;
      delete widgetData?.dashboardConfig;
      delete widgetData?.scopeSelectionData;
      delete widgetData?.widgetConfig;
      delete widgetData?.owner;
      delete widgetData?.x;
      delete widgetData?.commandPanelConfig
      delete widgetData?.y;
    }
 
    const formData = new FormData();
    formData.append(MESSAGE_FIELD, userMessage);
    formData.append(CHAT_PROFILE_FIELD, this.config.profileId);
    formData.append(CONTEXT, widgetData ? JSON.stringify(widgetData): '');
    this.mentions.forEach(mention => {
      formData.append(MENTIONS_FIELD, JSON.stringify(mention));
    })
    let selectedFile: File | Blob | null = this.selectedFile;
    if (this.config?.taskData?.data?.file) {
      selectedFile = this.config.taskData.data.file;
    }
    if (selectedFile) {
      formData.append(FILE_FIELD, selectedFile, (selectedFile as File)?.name ?? "capture.png");
    }
    this.addMessage(this.newUserMessage(userMessage, selectedFile));
    if (this.chatAgentType == AgentType.AI) {
      clearInterval(this.resetLiveAgentPendingTimeOutInterval); // Clearing the timeout on live agent end.
      clearInterval(this.resetLiveAgentReplyTimeOutInterval); // Clearing the timeout on live agent end.
      return this.chatService.submitMessageToAI(formData);
    } else {
      clearInterval(this.resetLiveAgentReplyTimeOutInterval);
      return this.chatService.submitMessageToHuman(formData, this.liveAgentConversationId);
    }
  }

  submitMessage(userMessage: string) {
    this.loading = true;
    this.startLastActiveTimeCheck(); // setting the interval again to check last active time every one minute.
    this.subs.sink = this.submitMessageToAgent(userMessage).pipe(
      tap((response) => {
        this.responseCounter++;
        if (this.responseCounter === 1) {
          this.recommendCharts.emit({ data: 'your first request data', raw: userMessage });
        }
        this.handleAgentResponse(response);
        this.loading = false;
      }),
      catchError((err) => {
        this.handleErrorResponse(err);
        this.loading = false;
        return EMPTY;
      })
    ).subscribe();
  }

  onClickHelp() {
    window.open(this.chatService.chatSupportUrl, "_blank");
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  onToggleSpeech() {
    this.speechEnabled = !this.speechEnabled;
    if (!this.speechEnabled && this.player) {
      this.player.pause();
    }
  }

  onToggleMic() {
    if (this.micEnabled) {
      this.recognizeSpeech();
    }
  }

  get micEnabled(): boolean {
    return this.chatInput?.micEnabled ?? false;
  }

  recognizeSpeech() {
    this.subs.sink = this.chatService.speechConfig.subscribe((speechConfig) => {
      const audioConfig = AudioConfig.fromDefaultMicrophoneInput();
      const speechRecognizer = new SpeechRecognizer(speechConfig, audioConfig);

      speechRecognizer.recognizeOnceAsync(result => {
        if (result.reason === ResultReason.RecognizedSpeech) {
          this.submitMessage(result.text);
        } else {
          console.error('Speech was cancelled or could not be recognized. Ensure your microphone is working properly.', result);
        }
        speechRecognizer.close();
        this.micDisabled();
      }, err => {
        this.micDisabled();

      });
    }, err => {
      this.micDisabled();
    })
  }

  micDisabled() {
    this.chatInput.micEnabled = false;
  }

  player: SpeakerAudioDestination;
  speechSynthesizer: SpeechSynthesizer;
  synthesizeSpeech(ai_response: string, language: string) {
    //If the player is already playing, pause it.
    if (this.player && this.speechSynthesizer) {
      this.player.pause();
    }
    this.player = new SpeakerAudioDestination();
    const audioConfig = AudioConfig.fromSpeakerOutput(this.player);
    this.subs.sink = this.chatService.speechConfig.subscribe((speechConfig) => {
      speechConfig.speechSynthesisLanguage = language;
      this.speechSynthesizer = new SpeechSynthesizer(speechConfig, audioConfig);
      this.speechSynthesizer.speakTextAsync(ai_response, result => {
        if (result) {
          this.speechSynthesizer.close();
        }
      },
        error => {
          console.error(error);
          this.speechSynthesizer.close();
        })
    })
  }

  /**
   * Determines if a message can prompt the user for feedback.
   * @param message Chat message
   * @returns boolean
   */
  canShowFeedback(message: Message) {
    return message._metadata!.showFeedbackPrompt && message.role !== Role.LIVE_AGENT;
  }

  /**
   * Opens image in a new tab
   * @param imageUrl 
   */
  openImageInNewTab(imageUrl: string | SafeUrl) {
    if (typeof imageUrl === 'object') {
      window.open(this.sanitizer.sanitize(4, imageUrl) || '', '_blank');
    } else {
      window.open(imageUrl, '_blank');
    }
  }
  /**
   * Sets an interval for idle check.
   * 
   * @param idleTime - The idle time in milliseconds.
   * @param checkTime - A function that returns the last active time.
   * @param idleStatusType - The type of idle status.
   * @returns The interval ID.
   */
  startIdleCheck(idleTime: number, checkTime: () => number, idleStatusType: IdleStatusType) {
    return setInterval(() => {
      const lastActiveTime = checkTime();
      if ((this.currentTime - lastActiveTime) >= idleTime) {
        this.getIdleStateMessage(idleStatusType);
      }
    }, 300);
  }

  /**
   * Sets an interval for updating the last active time.
   * This method clears the previous interval and sets a new one.
   * The interval is used to periodically check if the user is idle.
   * If the user is idle, it triggers the idle status type and performs a specific action.
   */
  startLastActiveTimeCheck() {
    clearInterval(this.resetLastActiveTimeOutInterval);
    this.resetLastActiveTimeOutInterval = this.startIdleCheck(
      IdleTimeOut.USER_IDLE,
      () => new Date(this.chatService.lastInteractedDatetime as string).getTime(),
      IdleStatusType.USER_IDLE
    );
  }

  /**
   * Sets the interval for checking the idle time of the live agent's reply.
   * Clears the previous interval if it exists.
   * @returns {void}
   */
  liveAgentReplyTimeInterval() {
    clearInterval(this.resetLiveAgentPendingTimeOutInterval);
    this.resetLiveAgentPendingTimeOutInterval = this.startIdleCheck(
      IdleTimeOut.AGENT_IDLE,
      () => this.awaitingLiveAgentTime.getTime(),
      IdleStatusType.AGENT_IDLE
    );
  }

  /**
   * Sets the interval for checking if the agent has timed out while awaiting a reply from the user.
   * Clears any existing interval before setting a new one.
   */
  userToAgentReplyTimeOutSetInterval() {
    clearInterval(this.resetLiveAgentReplyTimeOutInterval);
    this.resetLiveAgentReplyTimeOutInterval = this.startIdleCheck(
      IdleTimeOut.AGENT_TIMED_OUT,
      () => this.awaitingReplyTime.getTime(),
      IdleStatusType.AGENT_TIMED_OUT
    );
  }

  /**
   * Retrieves the idle state message based on the provided idle state.
   * 
   * @param idleState - The idle state type.
   */
  getIdleStateMessage(idleState: IdleStatusType) {
    let textContent = IdleStateMessage[idleState] as string;
    if (idleState == IdleStatusType.AGENT_TIMED_OUT) {
      clearInterval(this.resetLiveAgentReplyTimeOutInterval);
      this.cancelLiveAgentConnection(); // cancelling the live agent connection.
    } else if (idleState == IdleStatusType.AGENT_IDLE) {
      textContent = textContent.replace("{this.agentName}", this.aiAgentName); // replacing the agent name in the message.
      clearInterval(this.resetLiveAgentPendingTimeOutInterval);
      this.cancelLiveAgentConnection(); // cancelling the live agent connection.
    }
    clearInterval(this.resetLastActiveTimeOutInterval); // Clearing the interval on idle state, if the user is idle or agent is idle.
    this.addMessage(this.newAIMessage({ text: textContent }));
    this.disableInput = true;
  }

  cancelLiveAgentConnection() {
    this.chatAgentType = AgentType.AI; // changing the agent type to AI.
    this.chatStatus = ChatStatus.CHAT_ACTIVE; // changing the chat status to active.
    this.chatService.endAIChatSession().subscribe(); // ending the live agent chat session.
    this.chatToggle.onToggle(true); // changing the agent type to AI.
  }

  // Method to restrict the background scroll when scroll reaches the top or bottom of the chat window.
  onScroll(event: WheelEvent): void {
    const chatContent = this.chatContentElement.nativeElement;
    const scrollMargin = 2;
    // Check if the user is at the top or bottom of the chat-messages
    const isAtBottom = chatContent.scrollHeight - chatContent.scrollTop - chatContent.clientHeight <= scrollMargin;
    const isAtTop = chatContent.scrollTop <= scrollMargin;

    if ((isAtTop && event.deltaY < 0) || (isAtBottom && event.deltaY > 0)) {
      // Prevent background scroll when reached top or bottom of the chat window
      event.preventDefault();
    } else {
      chatContent.scrollTop += event.deltaY; // Ensure the scroll is contained within chat window
      event.preventDefault(); // Preventing the event propagation
    }
  }

  // Method to restrict the background scroll when scroll reaches the top or bottom of the chat window for mobile.
  onTouchMove(event: TouchEvent): void {
    const chatContent = this.chatContentElement.nativeElement;
    const scrollMargin = 2;
    const isAtBottom = chatContent.scrollHeight - chatContent.scrollTop - chatContent.clientHeight <= scrollMargin;
    const isAtTop = chatContent.scrollTop <= scrollMargin;

    const touchStart = event.touches[0].clientY;
    const touchEnd = event.changedTouches[0]?.clientY || touchStart;
    const touchDirection = touchStart - touchEnd;
    if ((isAtTop && touchDirection < 0) || (isAtBottom && touchDirection > 0)) {
      event.preventDefault();
    } else {
      chatContent.scrollTop += touchDirection;
    }
  }

  // Method to restrict the scroll behavior of the chat container.
  onContainerScroll(event: Event) {
    const target = event.target as HTMLElement;

    // Check if the target is not the chat-messages and mention-item list to restrict the scroll outside the chat-messages
    if (!target.closest('.chat-messages') && !target.closest('.mention-item')) {
      event.preventDefault();
    }
  }

  private async handleAgentResponse(response: ChatResponse): Promise<any> {
    /**
     * The agent responds to most queries in markdown.
     * However, certain responses such as visualization tasks yield stringified JSON responses.
     * Thus, the response may be in JSON or markdown format.
     * We first attempt to parse JSON. In the event that the parsing fails, we then parse to plain-text. 
    */
    if (response == null) {
      return;
    }
    if (response?.config) {
      this.chatScreenConfig = response.config as ChatConfigResponse;
    }

   if (response.responseType == ChatResponseType.ERROR) {
      const errorMessage = response.content as string;
      if (this.speechEnabled) {
        this.synthesizeSpeech(errorMessage, response.language);
      }
      this.addMessage(this.newAIMessage({ error: errorMessage }, response.messageId));
    } else if (response.responseType == ChatResponseType.DASHBOARD_WIDGET_MODIFY) {
      this.chartGenerated.emit(response);
      const textContent = response.content?.displayMessage as string;
      let markdown:any = await this.markdownService.parse(textContent);
      this.addMessage(this.newAIMessage({ text: markdown, quickSelectOptions: response.quickSelectOptions }, response.messageId));
    } else if (response.responseType == ChatResponseType.DASHBOARD_WIDGET_ACTION) {
      let textContent: string = "";
      const widgetResponse = response.content as DashboardWidgetActionResponse;
      if (widgetResponse.eventType == EventType.COMMAND) {
        switch (widgetResponse.actionType) {
          case ActionType.DASHBOARD_WIDGET_EXPORT_PNG:
            textContent = widgetResponse.displayMessage ?? "Exported widget as PNG ✅";
            this.addMessage(this.newAIMessage({ text: textContent }, response.messageId));
            break;
          case ActionType.DASHBOARD_WIDGET_EXPORT_EXCEL:
            textContent = widgetResponse.displayMessage ?? "Exported widget as Excel ✅";
            this.addMessage(this.newAIMessage({ text: textContent }, response.messageId));
            break;
          case ActionType.DASHBOARD_WIDGET_REMOVE:
            textContent = widgetResponse.displayMessage ?? "Removed widget from dashboard ✅";
            this.addMessage(this.newAIMessage({ text: textContent }, response.messageId));
            break;
          case ActionType.WIDGET_CREATE_NEW_OR_DUPLICATE:
            textContent = widgetResponse.displayMessage ?? "Widget Successfully Created as new ✅";
            this.addMessage(this.newAIMessage({ text: textContent }, response.messageId));
            break;
          default:
            textContent = "I don't recognize that action";
            this.addMessage(this.newAIMessage({ text: textContent }, response.messageId));
            break;
        }
        this.chatEventsService.broadcastEvent(widgetResponse);
      }
    } else if (response.responseType == ChatResponseType.FLOORPLAN_VARIATION_MODIFY) {
      this.variationGenerated.emit(response?.content);
      const textContent = response.content?.displayMessage as string;
      let markdown:any = await this.markdownService.parse(textContent);
      this.addMessage(this.newAIMessage({ text: markdown, quickSelectOptions: response.quickSelectOptions }, response.messageId));
     }
    else {
      const textContent = response.content as string;
      let markdown:any = await this.markdownService.parse(textContent);
      this.addMessage(this.newAIMessage({ text: markdown, quickSelectOptions: response.quickSelectOptions }, response.messageId));
      if (this.speechEnabled) {
        this.synthesizeSpeech(textContent, response.language);
      }

      if (this.isInternalPortal) {
        this.setInputCursor();
      }
    }
  }

  get isInternalPortal(): boolean {
    return this.configService.getConfig("appName") === HostRoute.INTERNAL;
  }

  private addMessage(message: Message) {
    this.activeMessage = message;
    this.messages.push(message);
  }

  private setInputCursor() {
    this.chatInput?.setInputCursor();
  }

  private handleErrorResponse(error: any) {
    const errorMessage = error?.error?.detail || "Sorry! Please try again." ;
    this.addMessage(this.newAIMessage({ text: errorMessage }));
  }

  private newUserMessage(text: string, file?: Blob | null, messageId: string = uuid.v4()): Message {
    let data: MessageData = { text: text };
    if (file?.type.startsWith("image")) {
      const mediaUrl = URL.createObjectURL(file);
      data.mediaUrl = this.sanitizer.bypassSecurityTrustUrl(mediaUrl);
    }
    return { role: Role.USER, data: data, _metadata: { messageId: messageId, showFeedbackPrompt: false } }
  }

  private newAIMessage(data: MessageData, messageId: string = uuid.v4()): Message {
    const message = { role: Role.AI, data: { ...data }, _metadata: { messageId: messageId, showFeedbackPrompt: false } }
    return message;
  }

  private scrollToBottom(): void {
    const element = this.chatContentElement?.nativeElement;
    if (element != null && element.scrollHeight > this._previousScrollHeight) {
      this._previousScrollHeight = element.scrollHeight;
      element.scrollTop = element.scrollHeight;
    }
  }

  private get currentTime(): number {
    return new Date().getTime();
  }
}
